import type { Activity } from '~/types/tShared';
import { AddPatientStatus, GetPatientsRequestBody } from '@tempus/trials-types';

import axios from '../axios';
import { V2_PATIENT_TRACKER, V2_TIME_PATIENTS } from '../constants';
import { Patient, PatientTrackerTabCounts, Physician, TimePatient } from '../types/patientTrackerStore';
import {
  CreatePatientAndTrackingRecord,
  GetPatientTrackerTabCountsRequestBody,
  GetTimePatientsCSVRequestBody,
  PatientFromStateflow,
  PatientSearchRequestBody,
  PatientTrackerBulkUpdates,
  UpdateTimePatientV2,
} from '../types/portal';
import { RescreenPatientCohortMatchReq, RescreenPatientCohortMatchRes } from '../types/timeScreeningApi';

export const patientTrackerApi = {
  getTabCounts: async (body: GetPatientTrackerTabCountsRequestBody): Promise<PatientTrackerTabCounts> => {
    return axios.post(`${V2_PATIENT_TRACKER}/tab-count`, body).then(({ data }) => data);
  },
  getPatientRecords: async (
    body: GetPatientsRequestBody,
  ): Promise<{ patients: PatientFromStateflow[]; patientCount: number }> => {
    return axios.post(`${V2_PATIENT_TRACKER}`, body).then(({ data }) => data);
  },
  getPatientRecordsCsv: async (body: Partial<GetTimePatientsCSVRequestBody>): Promise<Blob> => {
    return axios.post(`${V2_PATIENT_TRACKER}/csv`, body, { responseType: 'blob' }).then(({ data }) => data);
  },
  getPatientRecord: async (institutionId: string, timePatientId: string): Promise<PatientFromStateflow> => {
    return axios
      .get(`${V2_PATIENT_TRACKER}/institution/${institutionId}/?timePatientId=${timePatientId}`)
      .then(({ data }) => data);
  },
  createPatientAndTrackingRecord: async (
    institutionId: string,
    body: CreatePatientAndTrackingRecord,
  ): Promise<{ status: AddPatientStatus }> => {
    return axios.post(`${V2_PATIENT_TRACKER}/institution/${institutionId}/patient`, body).then(({ data }) => data);
  },
  getPatients: async (body: PatientSearchRequestBody): Promise<Patient[]> => {
    return axios.post(`${V2_PATIENT_TRACKER}/patient-search`, body).then(({ data }) => data);
  },
  getPatientTrackerReportTemplate: async (): Promise<ArrayBuffer> => {
    return axios
      .get(`${V2_PATIENT_TRACKER}/bulk-update`, {
        responseType: 'arraybuffer',
      })
      .then(({ data }) => data);
  },
  uploadBulkPatientTrackerReport: async (data: PatientTrackerBulkUpdates[], userId?: string): Promise<Buffer> => {
    return axios.post(`${V2_PATIENT_TRACKER}/bulk-update`, { data, userId });
  },
  rescreen: async (body: RescreenPatientCohortMatchReq): Promise<RescreenPatientCohortMatchRes> => {
    return axios.post(`${V2_PATIENT_TRACKER}/rescreen`, body).then(({ data }) => data);
  },
};

export const timePatientApi = {
  getActivities: async (timePatientId: string): Promise<Activity[]> => {
    return axios.get(`${V2_TIME_PATIENTS}/${timePatientId}/activities`).then(({ data }) => data);
  },
  getInstitutionPhysicians: async (timePatientId: string): Promise<Physician[]> => {
    return axios.get(`${V2_TIME_PATIENTS}/${timePatientId}/physicians`).then(({ data }) => data);
  },
  updateTimePatientV2: async (timePatientId: string, updates: UpdateTimePatientV2): Promise<TimePatient> => {
    return axios.patch(`${V2_TIME_PATIENTS}/${timePatientId}`, updates).then(({ data }) => data);
  },
};
